import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { compareAsc, getDay, addDays, startOfDay, format, parseISO } from 'date-fns';
import type AbilitiesService from 'ember-can/services/abilities';
import config from 'garaje/config/environment';
import type DeskLendingService from 'garaje/services/desk-lending';
import type { DeskLendingDateStatus, LendableDeskInfo } from 'garaje/services/desk-lending';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LocalStorageService from 'garaje/services/local-storage';
import type LocationFeatureFlagsService from 'garaje/services/location-feature-flags';
import type MessageBusService from 'garaje/services/message-bus';
import type PerformanceLoadTime from 'garaje/services/performance-load-time';
import type RegistrationsService from 'garaje/services/registrations';
import type ScheduleGraphqlService from 'garaje/services/schedule-graphql';
import type StateService from 'garaje/services/state';
import { LENDING_STATUS } from 'garaje/utils/enums';
import { getReadableScheduleEligibilityOffset } from 'garaje/utils/getReadableScheduleEligibilityOffset';
import urlBuilder from 'garaje/utils/url-builder';

type ScheduleTab = 'plan-your-week' | 'attendance';

export default class ScheduleIndexController extends Controller {
  @service declare state: StateService;
  @service declare registrations: RegistrationsService;
  @service declare localStorage: LocalStorageService;
  @service declare featureConfig: FeatureConfigService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare locationFeatureFlags: LocationFeatureFlagsService;
  @service declare abilities: AbilitiesService;
  @service declare performanceLoadTime: PerformanceLoadTime;
  @service declare deskLending: DeskLendingService;
  @service declare flashMessages: FlashMessagesService;
  @service declare messageBus: MessageBusService;
  @service declare scheduleGraphql: ScheduleGraphqlService;

  constructor(properties: object | undefined) {
    super(properties);
    this.messageBus.on('embedded-app-message', this, this.handleMessage);
  }

  isTesting = config.environment === 'test';

  queryParams = ['selectedInviteId'];

  @tracked selectedInviteId: string | null = null;
  @tracked lendableDesks: LendableDeskInfo[] = [];
  @tracked _lendingStatusByDate: Record<string, DeskLendingDateStatus> = {};
  @tracked activeTab: ScheduleTab = 'plan-your-week';
  @tracked loginRequired = false;
  @tracked multiDateModalIsOpen = false;
  @tracked lendPopupOpen = false;
  @tracked lendPopupDate: Date | null = null;

  get viewAttendanceOnWebEnabled(): boolean {
    return this.featureFlags.isEnabled('view-attendance-on-web');
  }

  get attendanceIframeUrl(): string {
    const companyId = this.state.currentCompany.id;
    const locationId = this.state.currentLocation.id;
    const employeeId = (this.model as { employeeId?: string }).employeeId;
    return urlBuilder.attendance.calendarUrl(companyId, locationId, this.loginRequired, employeeId);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  get days() {
    return this.registrations.days.sort((a: { date: Date }, b: { date: Date }) => compareAsc(a.date, b.date));
  }

  get isPartialDayEnabled(): boolean {
    return this.abilities.can('use partial day booking desk');
  }

  get showRequiredDocumentExpiredBanner(): boolean {
    const noRequiredDocumentApproval = this.days.find((day) => !day.hasRequiredDocumentApproval);
    return !!noRequiredDocumentApproval;
  }

  get hasDesks(): boolean {
    return ((this.state.currentLocationEnabledDesks as { desksQuantity?: number })?.desksQuantity ?? 0) > 0;
  }

  get desksEnabled(): boolean {
    const { currentLocationEnabledDesks } = this.state;
    return (
      !!(this.state.features as { canAccessDesksApplication?: boolean })?.canAccessDesksApplication &&
      !!currentLocationEnabledDesks &&
      !!(currentLocationEnabledDesks as { active?: boolean }).active
    );
  }

  get bookDeskEnabled(): boolean {
    return (
      this.desksEnabled &&
      this.screeningFlowEnabled &&
      this.hasDesks &&
      this.featureConfig.isEnabled('deskReservations.booking')
    );
  }

  get getGroupDeskReservationsEnabled(): boolean {
    return this.locationFeatureFlags.isEnabled('group-desk-reservations');
  }

  get deskLendingEnabled(): boolean {
    const hasAbility =
      this.abilities.can('lend desk for desks') &&
      !!(this.state.currentLocationEnabledDesks as { deskLendingEnabled?: boolean })?.deskLendingEnabled;
    if (!hasAbility) return false;

    if (this.featureFlags.isEnabled('reverse-hoteling-multiple-desks') && this.lendableDesks.length > 0) {
      return true;
    }
    // Fallback / old path: check model.assignedDeskId
    return !!(this.model as { assignedDeskId?: string }).assignedDeskId;
  }

  get hasMultipleDesks(): boolean {
    return this.lendableDesks.length > 1;
  }

  get multiDeskEnabled(): boolean {
    return (
      this.featureFlags.isEnabled('reverse-hoteling-multiple-desks') && this.deskLendingEnabled && this.hasMultipleDesks
    );
  }

  get primaryDeskId(): string | null {
    return (
      this.lendableDesks[0]?.deskId?.toString() ?? (this.model as { assignedDeskId?: string }).assignedDeskId ?? null
    );
  }

  // Per-desk per-date lookup: deskId string → dateKey → status
  get lendingStatusByDeskAndDate(): Record<string, Record<string, DeskLendingDateStatus>> {
    const map: Record<string, Record<string, DeskLendingDateStatus>> = {};
    for (const desk of this.lendableDesks) {
      const deskKey = String(desk.deskId);
      map[deskKey] = {};
      for (const dateStatus of desk.dates) {
        map[deskKey][dateStatus.date] = dateStatus;
      }
    }
    return map;
  }

  // Single-desk compat: first desk's lending status for existing day card components.
  // When multi-desk is enabled, DeskLendingCards handle lent state rendering,
  // so return empty to avoid duplicate lent-desk UI in the normal day card.
  get lendingStatusByDate(): Record<string, DeskLendingDateStatus> {
    if (this.multiDeskEnabled) return {};
    if (this.lendableDesks.length > 0) {
      return this.lendingStatusByDeskAndDate[String(this.lendableDesks[0]?.deskId)] ?? {};
    }
    // Old path: use tracked property populated by fetchLendingStatus
    return this._lendingStatusByDate;
  }

  get activeDays(): Array<{ dayOfWeek: string; active: boolean }> {
    const workplaceDays = (this.model as { workplaceDays?: Array<{ dayOfWeek: string; active: boolean }> })
      .workplaceDays;
    if (!workplaceDays) return [];
    return workplaceDays.filter((day) => day.active);
  }

  get screeningFlowEnabled(): boolean {
    return (this.state.currentLocation as { employeeScreeningEnabled?: boolean }).employeeScreeningEnabled ?? false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  get selectedDay() {
    // must check selectedInviteId because some screeningCards have an id of null in production
    if (!this.selectedInviteId) {
      return null;
    }

    // The day doesn't have an ID, so have to select on a related object's id
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return this.days.find((day) => day.screeningCard?.id == this.selectedInviteId);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  get indexedDays() {
    return this.days.map((day, index) => {
      return { ...day, index };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  get weeks() {
    const days = this.indexedDays;
    if (days.length == 0) return [];
    let i = 0;
    const daysLeftAtStart = 7 - getDay(days[0]!.date);
    const weeks = [days.slice(0, daysLeftAtStart)];
    i += daysLeftAtStart;
    while (i < days.length) {
      weeks.push(days.slice(i, i + 7));
      i += 7;
    }
    return weeks;
  }

  get shouldLoadDays(): boolean {
    const { scheduleEligibilityOffset } = this.state.currentLocation as { scheduleEligibilityOffset?: number | null };
    if (scheduleEligibilityOffset == null) {
      return true;
    }
    return this.daysToLoad > 0;
  }

  get daysToLoad(): number {
    const { scheduleEligibilityOffset } = this.state.currentLocation as { scheduleEligibilityOffset?: number | null };

    if (scheduleEligibilityOffset == null) {
      return 30;
    }

    const offsetInDays = -scheduleEligibilityOffset / 1440 - this.days.length;
    return Math.min(offsetInDays, 30);
  }

  get readableScheduleEligibilityOffset(): string {
    return getReadableScheduleEligibilityOffset(
      (this.state.currentLocation as { scheduleEligibilityOffset?: number }).scheduleEligibilityOffset!,
    );
  }

  get isScheduleEligibilityOffsetNotificationVisible(): boolean {
    const { scheduleEligibilityOffset } = this.state.currentLocation as { scheduleEligibilityOffset?: number | null };
    const offset = this.storedScheduleEligibilityOffset;

    return offset !== scheduleEligibilityOffset && scheduleEligibilityOffset !== null;
  }

  get storedScheduleEligibilityOffset(): number | undefined {
    const { id } = this.state.currentLocation as { id: string };

    try {
      const offsets = this.localStorage.getItem('schedule_eligibility_offsets');
      const parsed = (offsets ? JSON.parse(offsets) : []) as { locationId: string; offset: number }[];
      const match = parsed.find((l) => l.locationId === id);

      return match?.offset;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }

    return undefined;
  }

  removeStoredScheduleEligibilityOffset(): void {
    const { id } = this.state.currentLocation as { id: string };

    try {
      const offsets = this.localStorage.getItem('schedule_eligibility_offsets');
      const parsed = (offsets ? JSON.parse(offsets) : []) as { locationId: string; offset: number }[];
      const index = parsed.findIndex((l) => l.locationId === id);
      if (index > -1) {
        parsed.splice(index, 1);
      }

      this.localStorage.setItem('schedule_eligibility_offsets', JSON.stringify(parsed));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  handleMessage = (message: { event: string; type?: string; msg?: string }): void => {
    if (message.event === 'showToaster' && message.type && message.msg) {
      this.flashMessages.showAndHideFlash(message.type as 'success' | 'info' | 'error' | 'warning', message.msg);
    }
  };

  @action
  closeScheduleEligibilityOffsetNotification(): void {
    const { id, scheduleEligibilityOffset } = this.state.currentLocation as {
      id: string;
      scheduleEligibilityOffset: number;
    };
    const locationItem = { locationId: id, offset: scheduleEligibilityOffset };

    try {
      const offsets = this.localStorage.getItem('schedule_eligibility_offsets');
      const parsed = (offsets ? JSON.parse(offsets) : []) as { locationId: string; offset: number }[];
      const index = parsed.findIndex((l) => l.locationId === id);
      if (index > -1) {
        parsed.splice(index, 1, locationItem);
      } else {
        parsed.push(locationItem);
      }

      this.localStorage.setItem('schedule_eligibility_offsets', JSON.stringify(parsed));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  @action
  logLoadTime(): void {
    this.performanceLoadTime.logPageUsableMetric();
  }

  @action
  setActiveTab(tab: ScheduleTab): void {
    this.activeTab = tab;
  }

  @action
  trackLoggedIn(): void {
    const waLoggedIn = this.localStorage.getItem('wa_logged_in');

    if (!waLoggedIn) {
      this.loginRequired = true;
      this.localStorage.setItem('wa_logged_in', true);
    } else {
      this.loginRequired = false;
    }
  }

  @action
  openMultiDateModal(): void {
    this.multiDateModalIsOpen = true;
  }

  @action
  closeMultiDateModal(): void {
    this.multiDateModalIsOpen = false;
  }

  @action
  async bulkSchedule(dates: string[]): Promise<void> {
    const deskId = this.primaryDeskId;

    const results = await Promise.allSettled(
      dates.map(async (dateStr) => {
        const date = parseISO(dateStr);
        const lendingStatus =
          this.lendingStatusByDeskAndDate[deskId ?? '']?.[dateStr]?.lendingStatus ??
          this.lendingStatusByDate[dateStr]?.lendingStatus;

        if (lendingStatus === 'LENT_AVAILABLE' && deskId) {
          await this.scheduleGraphql.reclaimDeskReservation({ deskId, date });
          this.updateLendingStatusForDate(date, LENDING_STATUS.NOT_LENT);

          const updated = await this.scheduleGraphql.fetchPartialDayRegistrationByDate({ date });
          if (updated) {
            await this.registrations.updateRegistration(date, (day) => ({
              ...day,
              ...updated,
              date,
              maxOccupancy: updated.maxOccupancy ?? undefined,
            }));
          }
        } else {
          await this.registrations.reserveDeskPartialDay(date, { deskId });
        }
      }),
    );

    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;

    if (failed === 0) {
      this.flashMessages.showAndHideFlash('success', `Scheduled ${succeeded} day${succeeded === 1 ? '' : 's'}`);
    } else {
      this.flashMessages.showAndHideFlash('error', `Scheduled ${succeeded} of ${dates.length} days. ${failed} failed.`);
    }
  }

  @action
  async bulkLend(dates: string[], deskIds?: string[]): Promise<void> {
    const targetDeskIds = deskIds ?? (this.primaryDeskId ? [this.primaryDeskId] : []);
    if (targetDeskIds.length === 0) return;

    let totalLent = 0;
    let totalAlreadyLent = 0;
    let totalFailed = 0;

    await Promise.allSettled(
      targetDeskIds.map(async (currentDeskId) => {
        const deskStatus = this.lendingStatusByDeskAndDate[currentDeskId] ?? this.lendingStatusByDate;
        const alreadyLent = dates.filter((d) => {
          const status = deskStatus[d]?.lendingStatus;
          return status === LENDING_STATUS.LENT_AVAILABLE || status === LENDING_STATUS.LENT_BOOKED;
        });
        const datesToLend = dates.filter((d) => !alreadyLent.includes(d));

        totalAlreadyLent += alreadyLent.length;
        if (datesToLend.length > 0) {
          try {
            await this.deskLending.lendDeskBulk(currentDeskId, datesToLend);
            totalLent += datesToLend.length;
          } catch {
            totalFailed += datesToLend.length;
          }
        }
      }),
    );

    await this.refreshLendingData();

    if (totalFailed > 0) {
      this.flashMessages.showAndHideFlash('error', `Released ${totalLent} desk-days. ${totalFailed} failed.`);
    } else if (totalLent === 0 && totalAlreadyLent > 0) {
      this.flashMessages.showAndHideFlash(
        'success',
        `Already lent for ${totalAlreadyLent} desk-day${totalAlreadyLent === 1 ? '' : 's'}`,
      );
    } else if (totalAlreadyLent > 0) {
      this.flashMessages.showAndHideFlash(
        'success',
        `Released ${totalLent} desk-day${totalLent === 1 ? '' : 's'}. ${totalAlreadyLent} already lent.`,
      );
    } else {
      this.flashMessages.showAndHideFlash('success', `Released ${totalLent} desk-day${totalLent === 1 ? '' : 's'}`);
    }
  }

  async refreshLendingData(): Promise<void> {
    if (this.featureFlags.isEnabled('reverse-hoteling-multiple-desks')) {
      await this.fetchLendableDesks();
    } else {
      await this.fetchLendingStatus();
    }
  }

  @action
  async fetchLendableDesks(startDate?: Date, endDate?: Date): Promise<void> {
    if (!(this.model as { deskLendingAvailable?: boolean }).deskLendingAvailable) return;

    const resolvedStart = startDate ?? startOfDay(new Date());
    const resolvedEnd =
      endDate ??
      (this.days.length > 0
        ? addDays(this.days[this.days.length - 1]!.date, 1)
        : addDays(resolvedStart, this.daysToLoad));

    const response = await this.deskLending.fetchMyLendableDesks(
      (this.state.currentLocation as { id: string }).id,
      resolvedStart,
      resolvedEnd,
    );

    // Merge new date data into existing desks (keyed by deskId + date) to preserve
    // earlier weeks' lending state during infinite scroll.
    if (this.lendableDesks.length === 0) {
      this.lendableDesks = response.desks;
    } else {
      const existingByDeskId: Record<number, LendableDeskInfo> = {};
      for (const desk of this.lendableDesks) {
        existingByDeskId[desk.deskId] = desk;
      }
      for (const newDesk of response.desks) {
        const existing = existingByDeskId[newDesk.deskId];
        if (existing) {
          const datesByKey: Record<string, DeskLendingDateStatus> = {};
          for (const d of existing.dates) {
            datesByKey[d.date] = d;
          }
          for (const dateStatus of newDesk.dates) {
            datesByKey[dateStatus.date] = dateStatus;
          }
          existingByDeskId[newDesk.deskId] = { ...existing, dates: Object.values(datesByKey) };
        } else {
          existingByDeskId[newDesk.deskId] = newDesk;
        }
      }
      this.lendableDesks = Object.values(existingByDeskId);
    }
  }

  // Old path: per-desk lending status fetch (used when reverse-hoteling-multiple-desks is off)
  @action
  async fetchLendingStatus(startDate?: Date, endDate?: Date): Promise<void> {
    const deskId = (this.model as { assignedDeskId?: string }).assignedDeskId;
    if (!deskId) return;
    const resolvedStart = startDate ?? startOfDay(new Date());
    const resolvedEnd =
      endDate ??
      (this.days.length > 0
        ? addDays(this.days[this.days.length - 1]!.date, 1)
        : addDays(resolvedStart, this.daysToLoad));

    try {
      const response = await this.deskLending.fetchLendingStatus(deskId, resolvedStart, resolvedEnd);
      const statusMap: Record<string, DeskLendingDateStatus> = {};
      for (const dateStatus of response.dates) {
        statusMap[dateStatus.date] = dateStatus;
      }
      this._lendingStatusByDate = { ...this._lendingStatusByDate, ...statusMap };
    } catch {
      // If lending status fetch fails, desk lending UI will show default (NOT_LENT) state
    }
  }

  @action
  async loadMoreAndFetchLending(daysToLoad: number): Promise<void> {
    const prevDayCount = this.days.length;
    await this.registrations.loadMoreTask.perform(daysToLoad);
    if (!this.deskLendingEnabled) return;
    const newDays = this.days.slice(prevDayCount);
    if (newDays.length === 0) return;
    const startDate = newDays[0]!.date;
    const endDate = addDays(newDays[newDays.length - 1]!.date, 1);

    if (this.featureFlags.isEnabled('reverse-hoteling-multiple-desks')) {
      await this.fetchLendableDesks(startDate, endDate);
    } else {
      await this.fetchLendingStatus(startDate, endDate);
    }
  }

  @action
  updateLendingStatusForDate(date: Date, status: string, deskId?: string): void {
    const dateKey = format(date, 'yyyy-MM-dd');

    if (this.lendableDesks.length > 0) {
      // Multi-desk path: update within lendableDesks array
      const targetDeskId = deskId ?? this.primaryDeskId;
      if (!targetDeskId) return;
      this.lendableDesks = this.lendableDesks.map((desk) => {
        if (String(desk.deskId) !== targetDeskId) return desk;
        return {
          ...desk,
          dates: desk.dates.map((d) =>
            d.date === dateKey ? { ...d, lendingStatus: status as DeskLendingDateStatus['lendingStatus'] } : d,
          ),
        };
      });
    } else {
      // Old path: update tracked status map directly
      this._lendingStatusByDate = {
        ...this._lendingStatusByDate,
        [dateKey]: { date: dateKey, lendingStatus: status } as DeskLendingDateStatus,
      };
    }
  }

  get lendPopupDateKey(): string {
    return this.lendPopupDate ? format(this.lendPopupDate, 'yyyy-MM-dd') : '';
  }

  getLendingStatusForDeskAndDate = (desk: LendableDeskInfo, date: Date): DeskLendingDateStatus | undefined => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return this.lendingStatusByDeskAndDate[String(desk.deskId)]?.[dateKey];
  };

  isScheduledForDate = (date: Date): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const day = this.days.find((d: { date: Date }) => format(d.date, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd'));
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return !!(day?.screeningCard?.id || day?.reservation || (day?.reservations?.length ?? 0) > 0);
  };

  private isDeskLentByOthers(deskId: number | string, dateKey: string): boolean {
    const entry = this.lendingStatusByDeskAndDate[String(deskId)]?.[dateKey];
    if (!entry) return false;
    const { lendingStatus, reservation } = entry;
    if (lendingStatus === LENDING_STATUS.LENT_AVAILABLE) return true;
    if (lendingStatus === LENDING_STATUS.LENT_BOOKED) {
      const currentEmployeeId = (this.model as { employeeId?: string }).employeeId;
      return String(reservation?.bookedByEmployeeId) !== String(currentEmployeeId);
    }
    return false;
  }

  hasLentDesksForDate = (date: Date): boolean => {
    if (!this.multiDeskEnabled) return false;
    const dateKey = format(date, 'yyyy-MM-dd');
    return this.lendableDesks.some((desk) => this.isDeskLentByOthers(desk.deskId, dateKey));
  };

  private getScheduledDeskIds(date: Date): Set<string> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const day = this.days.find((d: { date: Date }) => format(d.date, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd'));
    const ids = new Set<string>();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (day?.reservation?.desk?.id) ids.add(String(day.reservation.desk.id));
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (day?.reservations) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      for (const res of day.reservations) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (res?.desk?.id) ids.add(String(res.desk.id));
      }
    }
    return ids;
  }

  desksForDate = (date: Date): LendableDeskInfo[] => {
    if (!this.multiDeskEnabled) return [];
    const dateKey = format(date, 'yyyy-MM-dd');
    const scheduledDeskIds = this.getScheduledDeskIds(date);
    return this.lendableDesks.filter((desk) => {
      // Exclude desks the user is currently scheduled into
      if (scheduledDeskIds.has(String(desk.deskId))) return false;
      // Include desks lent by others or not lent
      return (
        this.isDeskLentByOthers(desk.deskId, dateKey) ||
        !this.lendingStatusByDeskAndDate[String(desk.deskId)]?.[dateKey]?.lendingStatus ||
        this.lendingStatusByDeskAndDate[String(desk.deskId)]?.[dateKey]?.lendingStatus === LENDING_STATUS.NOT_LENT
      );
    });
  };

  @action
  async unlendDesk(deskId: string | number, date: Date): Promise<void> {
    await this.deskLending.unlendDesk(String(deskId), date);
    await this.fetchLendableDesks();
  }

  @action
  async lendSingleDesk(deskId: string | number, date: Date): Promise<void> {
    await this.deskLending.lendDesk(String(deskId), date);
    await this.fetchLendableDesks();
  }

  @action
  async reclaimDesk(deskId: string | number, date: Date): Promise<void> {
    await this.scheduleGraphql.reclaimDeskReservation({ deskId: String(deskId), date });
    await this.fetchLendableDesks();
    // Refresh registration data for the day (partial-day returns plural `reservations`)
    const updated = await this.scheduleGraphql.fetchPartialDayRegistrationByDate({ date });
    if (updated) {
      await this.registrations.updateRegistration(date, (day) => ({
        ...day,
        ...updated,
        date,
        maxOccupancy: updated.maxOccupancy ?? undefined,
      }));
    }
  }

  @action
  async scheduleWithDesk(deskId: string | number, date: Date): Promise<void> {
    const deskIdStr = String(deskId);
    const dateKey = format(date, 'yyyy-MM-dd');
    const lendingStatus = this.lendingStatusByDeskAndDate[deskIdStr]?.[dateKey]?.lendingStatus;

    if (lendingStatus === LENDING_STATUS.LENT_AVAILABLE) {
      await this.reclaimDesk(deskId, date);
    } else {
      await this.registrations.reserveDeskPartialDay(date, { deskId: deskIdStr });
      await this.fetchLendableDesks();
    }
  }

  // Lend popup actions
  @action
  openLendPopup(date: Date): void {
    this.lendPopupDate = date;
    this.lendPopupOpen = true;
  }

  @action
  closeLendPopup(): void {
    this.lendPopupOpen = false;
    this.lendPopupDate = null;
  }

  @action
  async confirmLendPopup(deskIds: string[]): Promise<void> {
    if (!this.lendPopupDate || deskIds.length === 0) return;

    const date = this.lendPopupDate;
    const results = await Promise.allSettled(deskIds.map((deskId) => this.deskLending.lendDesk(deskId, date)));

    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;

    await this.fetchLendableDesks();
    this.closeLendPopup();

    if (failed === 0) {
      const deskWord = succeeded === 1 ? 'desk' : 'desks';
      this.flashMessages.showAndHideFlash('success', `Released ${succeeded} ${deskWord}`);
    } else {
      this.flashMessages.showAndHideFlash(
        'error',
        `Released ${succeeded} of ${deskIds.length} desks. ${failed} failed.`,
      );
    }
  }
}
